import { createRouter, createWebHistory } from "vue-router";
import { nextTick } from "vue";
import { useUserStore } from "@/pinia/userStore";
import { auditsGuard } from "@/router/guards";
import RfDashboard from "@/views/RfDashboard.vue";
import RfSegmentView from "@/views/RfSegmentView.vue";
import Rf404 from "@/views/Rf404.vue";
import RfSettings from "@/views/RfSettings.vue";
import RfSettingsCompany from "@/views/RfSettingsCompany.vue";
import RfCompanyAccessManagement from "@/views/RfCompanyAccessManagement.vue";
import RfSettingsGlobal from "@/views/RfSettingsGlobal.vue";
import RfLogin from "@/views/RfLogin.vue";
import RfLoginResetInvitation from "@/views/RfLoginResetInvitation.vue";
import RfRegister from "@/views/RfRegister.vue";
import RfAppCreate from "@/views/RfAppCreate.vue";
import RfAppDashboard from "@/views/RfDashboard/index.vue";
import RfRetentionView from "@/views/RfPrompt/index.vue";
import RfRetentionCreative from "@/views/RfRetentionCreative.vue";
import RfRetentionExperimentView from "@/views/RfRetentionExperimentView.vue";
import RfPlacementView from "@/views/RfPlacementView.vue";
import RfPrompts from "@/views/RfPrompts/index.vue";
import RfAwsMarketplace from "@/views/RfAwsMarketplace.vue";
import RfSequenceView from "@/views/RfSequenceView.vue";
import RfGuides from "@/views/RfGuides/index.vue";
import RfLiveView from "@/views/RfLiveView.vue";
import RfSegments from "@/views/RfSegments/index.vue";
import RfStripeConnect from "@/views/RfStripeConnect.vue";
import RfFigmaConnect from "@/views/RfFigmaConnect.vue";
import RfAudits from "@/views/RfAudits/index.vue";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/invitation",
      component: RfLoginResetInvitation,
      meta: { title: "Invitation" },
    },
    {
      path: "/register",
      component: RfRegister,
      meta: { title: "Register" },
    },
    {
      path: "/password",
      component: RfLoginResetInvitation,
      meta: { title: "Reset Password" },
    },
    {
      path: "/login",
      component: RfLogin,
      meta: { title: "Login" },
    },
    {
      path: "/aws_marketplace/:token",
      component: RfAwsMarketplace,
    },
    // TODO- make these restful
    {
      path: "/",
      name: "Home",
      component: RfDashboard,
      children: [
        {
          path: "apps/:aid/dashboard",
          name: "App Dashboard",
          component: RfAppDashboard,
          meta: { title: "Dashboard" },
        },
        {
          path: "apps/:aid/segments",
          name: "All Segments",
          component: RfSegments,
          meta: { title: "Segments" },
        },
        {
          path: "apps/:aid/segments/:sid/:pathMatch(.*)*",
          name: "Segment Detail",
          component: RfSegmentView,
        },
        {
          path: "settings/company",
          name: "Company Settings",
          component: RfSettingsCompany,
          meta: { title: "Company Settings" },
        },
        {
          path: "settings/company-access",
          name: "Company Access",
          component: RfCompanyAccessManagement,
          meta: { title: "Company Access" },
          beforeEnter: async (to, from) => {
            const userStore = useUserStore();
            // Ensure user data is loaded
            if (!userStore.currUser?.id) {
              await userStore.getUser();
            }
            if (userStore.isUserCorpIt) {
              return true;
            } else {
              return { name: "Home" };
            }
          },
        },
        {
          path: "settings/global",
          name: "Global Settings",
          component: RfSettingsGlobal,
          meta: { title: "Global Settings" },
          beforeEnter: async (to, from) => {
            const userStore = useUserStore();
            // Ensure user data is loaded
            if (!userStore.currUser?.id) {
              await userStore.getUser();
            }
            if (userStore.isUserSuperAdmin) {
              return true;
            } else {
              return { name: "Home" };
            }
          },
        },
        {
          path: "audits",
          name: "Audit Logs",
          component: RfAudits,
          meta: { title: "Audit Logs" },
          beforeEnter: auditsGuard,
        },
        {
          path: "apps/:aid/settings/:stab?/:additional?",
          name: "Settings",
          component: RfSettings,
        },
        {
          path: "newapp",
          name: "New App",
          component: RfAppCreate,
          meta: { title: "Create App" },
        },
        {
          path: "apps/:aid/retentions/:pid/creative",
          name: "Retention Creative",
          component: RfRetentionCreative,
        },
        {
          path: "apps/:aid/retentions/:pid/experiments/:eid",
          name: "Retention Experiment View",
          component: RfRetentionExperimentView,
          beforeEnter: (to, from) => {
            if (from.params.sid) to.params.sid = from.params.sid;
          },
        },
        // NOTE- can't use aliases for these b/c we need to access all path params
        ...[
          { path: "apps/:aid/retentions/:pid/:tslug(.*)*", place: "" },
          {
            path: "apps/:aid/experiences/:sid/promotions/:pid/:tslug(.*)*",
            place: "Guide's ",
          },
          {
            path: "apps/:aid/placements/:plid/promotions/:pid/:tslug(.*)*",
            place: "Placements's ",
          },
        ].map(({ path, place }) => ({
          path,
          name: `${place}Retention Detail`,
          component: RfRetentionView,
        })),
        {
          path: "apps/:aid/placements/:pid",
          name: "Placement Detail",
          component: RfPlacementView,
        },
        {
          path: "apps/:aid/experiences",
          name: "All Guides",
          component: RfGuides,
          meta: { title: "Guides" },
        },
        {
          path: "apps/:aid/agent/:sid",
          name: "Agent Detail",
          component: RfSequenceView,
        },
        {
          path: "apps/:aid/experiences/:sid",
          name: "Guide Detail",
          component: RfSequenceView,
        },
        {
          path: "apps/:aid/prompts",
          name: "All Prompts",
          component: RfPrompts,
          meta: { title: "Prompts" },
        },
        {
          path: "apps/:aid/live",
          name: "Live",
          component: RfLiveView,
          meta: { title: "Live" },
        },
        {
          path: "apps/:aid/stripe-success/:destination?",
          name: "Stripe Connect Success",
          component: RfStripeConnect,
        },
        {
          path: "apps/:aid/stripe-error/:destination?",
          name: "Stripe Connect Error",
          component: RfStripeConnect,
        },
        {
          path: "apps/:aid/figma-success",
          name: "Figma Connect Success",
          component: RfFigmaConnect,
        },
        {
          path: "apps/:aid/figma-error",
          name: "Figma Connect Error",
          component: RfFigmaConnect,
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: Rf404,
      meta: { title: "Page Not Found" },
    },
    {
      path: "/:pathMatch(.*)",
      name: "bad-not-found",
      component: Rf404,
      meta: { title: "Page Not Found" },
    },
  ],

  scrollBehavior: (_t, _f, savedPosition) =>
    savedPosition || new Promise(res => res({ left: 0, top: 0 })),
});

router.afterEach(to => {
  if (to.meta.title) {
    const newTitle = `${to.meta.title} - Recurly Engage`;
    nextTick(() => (document.title = newTitle));
  }
});

export default router;
